import React from 'react';

import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import InputMask from '@amzn/meridian/input/mask';
import Row from '@amzn/meridian/row';

import { IVendorCode } from 'src/types/IVendor';
import { dateYyyyMmDdMask } from 'src/util/inputMask';

import './VendorCodes.scss';

interface IVendorCodesProps {
    vendorCodes: IVendorCode[];
    addVendorCode: () => void;
    removeVendorCode: (vendorCodeId: string) => void;
    updateVendorCode: (vendorCode: IVendorCode, value: string, field: string, vendorCodeId: string) => void;
    validateField: (field: string, vendorCodeId?: string) => void;
    errorMessages: Record<string, string>;
}

export const VendorCodes = ({
    vendorCodes,
    addVendorCode,
    removeVendorCode,
    updateVendorCode,
    validateField,
    errorMessages
}: IVendorCodesProps) => {
    const catalogCodeMask = new InputMask('xxxx');

    return (
        <Column>
            <Heading level={4}>Vendor codes</Heading>
            {vendorCodes.map((vendorCode) => (
                <Row key={vendorCode.vendorCodeId} alignmentVertical="top" widths={['15%', '15%', '15%', '15%']}>
                    <Input
                        label="Catalog code *"
                        value={vendorCode.catalogCode}
                        onChange={(value) =>
                            updateVendorCode(vendorCode, value.toUpperCase(), 'catalogCode', vendorCode.vendorCodeId)
                        }
                        onBlur={() => validateField('catalogCode', vendorCode.vendorCodeId)}
                        error={!!errorMessages[`vendorCodes${vendorCode.vendorCodeId}catalogCode`]}
                        errorMessage={errorMessages[`vendorCodes${vendorCode.vendorCodeId}catalogCode`]}
                        mask={catalogCodeMask}
                        type="text"
                        size="large"
                    />
                    <Input
                        label="Royalty code *"
                        value={vendorCode.royaltyCode}
                        onChange={(value) =>
                            updateVendorCode(
                                vendorCode,
                                value.toUpperCase().trim(),
                                'royaltyCode',
                                vendorCode.vendorCodeId
                            )
                        }
                        onBlur={() => validateField('royaltyCode', vendorCode.vendorCodeId)}
                        error={!!errorMessages[`vendorCodes${vendorCode.vendorCodeId}royaltyCode`]}
                        errorMessage={errorMessages[`vendorCodes${vendorCode.vendorCodeId}royaltyCode`]}
                        type="text"
                        size="large"
                    />
                    <Input
                        label="Mapping start *"
                        value={vendorCode.mappingStart}
                        onChange={(value) =>
                            updateVendorCode(vendorCode, value, 'mappingStart', vendorCode.vendorCodeId)
                        }
                        onBlur={() => validateField('mappingStart', vendorCode.vendorCodeId)}
                        error={!!errorMessages[`vendorCodes${vendorCode.vendorCodeId}mappingStart`]}
                        errorMessage={errorMessages[`vendorCodes${vendorCode.vendorCodeId}mappingStart`]}
                        mask={dateYyyyMmDdMask}
                        type="text"
                        size="large"
                        placeholder="YYYY-MM-DD"
                    />
                    <Input
                        label="Mapping end"
                        value={vendorCode.mappingEnd}
                        onChange={(value) => updateVendorCode(vendorCode, value, 'mappingEnd', vendorCode.vendorCodeId)}
                        onBlur={() => validateField('mappingEnd', vendorCode.vendorCodeId)}
                        error={!!errorMessages[`vendorCodes${vendorCode.vendorCodeId}mappingEnd`]}
                        errorMessage={errorMessages[`vendorCodes${vendorCode.vendorCodeId}mappingEnd`]}
                        mask={dateYyyyMmDdMask}
                        type="text"
                        size="large"
                        placeholder="YYYY-MM-DD"
                    />
                    {vendorCodes.length > 1 && (
                        <div className="remove-vendor-code-btn">
                            <Button type="link" size="small" onClick={() => removeVendorCode(vendorCode.vendorCodeId)}>
                                <Icon tokens={minusCircleTokens} />
                            </Button>
                        </div>
                    )}
                </Row>
            ))}
            <div>
                <Button type="link" size="small" onClick={addVendorCode}>
                    <Icon tokens={plusCircleTokens} />
                    Add another row
                </Button>
            </div>
        </Column>
    );
};
